import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Attach workspace header for super_admin workspace switching
  const wsId = localStorage.getItem('activeWorkspaceId');
  if (wsId) config.headers['X-Workspace-Id'] = wsId;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('activeWorkspaceId');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const systemHealthAPI = {
  get: () => api.get('/system-health'),
  getRun: (id) => api.get('/system-health/' + id),
  run: () => api.post('/system-health/run', null, { timeout: 120000 }),
  sendAlert: (id) => api.post('/system-health/' + id + '/alert'),
  sendTestEmail: (to) => api.post('/system-health/test-email', { to }),
};

export const authAPI = {
  login: (d) => api.post('/auth/login', d),
  register: (d) => api.post('/auth/register', d),
  getMe: () => api.get('/auth/me'),
  updateProfile: (d) => api.put('/auth/profile', d),
  changePassword: (d) => api.put('/auth/change-password', d),
  forgotPassword: (d) => api.post('/auth/forgot-password', d),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

export const workspaceAPI = {
  getAll: (p) => api.get('/workspaces', { params: p }),
  getById: (id) => api.get('/workspaces/' + id),
  create: (d) => api.post('/workspaces', d),
  update: (id, d) => api.put('/workspaces/' + id, d),
  delete: (id) => api.delete('/workspaces/' + id),
  getStats: () => api.get('/workspaces/stats'),
  getMine: () => api.get('/workspaces/mine'),
  switchWorkspace: (d) => api.post('/workspaces/switch', d),
  assignUsers: (id, d) => api.post('/workspaces/' + id + '/assign-users', d),
  getPlanFeatures: () => api.get('/workspaces/plan-features'),
};

export const userAPI = {
  getAll: (p) => api.get('/users', { params: p }),
  getById: (id) => api.get('/users/' + id),
  create: (d) => api.post('/users', d),
  update: (id, d) => api.put('/users/' + id, d),
  approve: (id) => api.put('/users/' + id + '/approve'),
  reject: (id) => api.put('/users/' + id + '/reject'),
  delete: (id) => api.delete('/users/' + id),
  getPending: () => api.get('/users/pending'),
  getStats: () => api.get('/users/stats'),
};

export const dashboardAPI = {
  getOverview: (p) => api.get('/dashboard/overview', { params: p }),
  getAIInsights: () => api.get('/dashboard/ai-insights'),
  getHealthEquity: () => api.get('/dashboard/health-equity'),
};

export const diseaseAPI = {
  getAll: (p) => api.get('/diseases', { params: p }),
  getById: (id) => api.get('/diseases/' + id),
  create: (d) => api.post('/diseases', d),
  update: (id, d) => api.put('/diseases/' + id, d),
  addNote: (id, d) => api.post('/diseases/' + id + '/notes', d),
  delete: (id) => api.delete('/diseases/' + id),
  getStats: () => api.get('/diseases/stats'),
  bulkImport: (formData) => api.post('/diseases/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const assessmentAPI = {
  getTemplates: (p) => api.get('/assessments/templates', { params: p }),
  getTemplateById: (id) => api.get('/assessments/templates/' + id),
  createTemplate: (d) => api.post('/assessments/templates', d),
  updateTemplate: (id, d) => api.put('/assessments/templates/' + id, d),
  deleteTemplate: (id) => api.delete('/assessments/templates/' + id),
  getResponses: (p) => api.get('/assessments/responses', { params: p }),
  getResponseById: (id) => api.get('/assessments/responses/' + id),
  submitResponse: (d) => api.post('/assessments/responses', d),
  getStats: () => api.get('/assessments/responses/stats'),
};

export const notificationAPI = {
  getAll: (p) => api.get('/notifications', { params: p }),
  markAsRead: (id) => api.put('/notifications/' + id + '/read'),
  markAllAsRead: () => api.put('/notifications/read-all'),
  delete: (id) => api.delete('/notifications/' + id),
  broadcast: (d) => api.post('/notifications/broadcast', d),
};

export const reportAPI = {
  getAll: (p) => api.get('/reports', { params: p }),
  getById: (id) => api.get('/reports/' + id),
  generate: (d) => api.post('/reports/generate', d),
  update: (id, d) => api.put('/reports/' + id, d),
  delete: (id) => api.delete('/reports/' + id),
};

export const programAPI = {
  getAll: (p) => api.get('/programs', { params: p }),
  getById: (id) => api.get('/programs/' + id),
  create: (d) => api.post('/programs', d),
  update: (id, d) => api.put('/programs/' + id, d),
  delete: (id) => api.delete('/programs/' + id),
};

export const vaccinationAPI = {
  getAll: (p) => api.get('/vaccinations', { params: p }),
  create: (d) => api.post('/vaccinations', d),
  getStats: () => api.get('/vaccinations/stats'),
  bulkImport: (formData) => api.post('/vaccinations/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const vitalAPI = {
  getAll: (p) => api.get('/vitals', { params: p }),
  create: (d) => api.post('/vitals', d),
  getStats: () => api.get('/vitals/stats'),
  bulkImport: (formData) => api.post('/vitals/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const sdohAPI = {
  getAll: (p) => api.get('/sdoh', { params: p }),
  create: (d) => api.post('/sdoh', d),
  getStats: () => api.get('/sdoh/stats'),
  bulkImport: (formData) => api.post('/sdoh/bulk-import', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const auditAPI = {
  getLogs: (p) => api.get('/audit', { params: p }),
  getStats: () => api.get('/audit/stats'),
};

export const aiAPI = {
  query: (d) => api.post('/ai/query', d),
  anomalies: () => api.get('/ai/anomalies'),
  insights: (d) => api.post('/ai/insights', d),
};

export default api;
